/* eslint-disable no-underscore-dangle */
import type { IdsPopupElementRef } from '../../components/ids-popup/ids-popup-attributes';
import { IdsConstructor } from '../../core/ids-element';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { EventsMixinInterface } from '../ids-events-mixin/ids-events-mixin';
import debounce from '../../utils/ids-debounce-utils/ids-debounce-utils';
import { requestAnimationTimeout } from '../../utils/ids-timer-utils/ids-timer-utils';
import { isWidthBelow } from '../../utils/ids-breakpoint-utils/ids-breakpoint-utils';

interface PopupOpener {
  onOutsideClick?(e: Event): void;
}

type Constraints = IdsConstructor<EventsMixinInterface & PopupOpener>;

/**
 * This mixin can be used with the IdsPopup component to provide event handling in some scenarios:
 * - When clicking outside the Popup occurs, an event handler at the document level hides the Popup.
 * @mixin IdsPopupOpenEventsMixin
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsPopupOpenEventsMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  static get attributes() {
    return [...(superclass as any).attributes];
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeOpenEvents(true);
    this.popupOpenEventsTarget = null;
  }

  /**
   * @property {boolean} hasOpenEvents true if "open" events are currently applied to this component
   */
  hasOpenEvents = false;

  /**
   * The target element that receives the top-level event listener
   * that will cause the `onOutsideClick` callback to fire
   * @type {IdsPopupElementRef}
   */
  popupOpenEventsTarget: IdsPopupElementRef = document.body;

  /**
   * @property {IdsPopupElementRef} currentPopupOpenEventsTarget stores a reference to the event
   *  target to be used for unbinding events (prevents memory leaks if the
   *  event target changes while the Popup is open)
   */
  #currentPopupOpenEventsTarget: IdsPopupElementRef = null;

  #scrollContainer: Element | null = null;

  #popupContainer: Element | null = null;

  /**
   * Finds scrollable container for dropdown elements
   * @param {HTMLElement} dropdownElem The dropdown element to start searching from
   * @param {Element | null} boundary The boundary element to stop searching at
   * @returns {HTMLElement | null} The scrollable container or null
   */
  #findScrollableContainer(dropdownElem: HTMLElement, boundary: Element | null): HTMLElement | null {
    let scrollableContainer: HTMLElement | null = dropdownElem;
    let depth = 0;
    const maxDepth = 50; // Prevent infinite loops
    while (scrollableContainer && scrollableContainer !== boundary && depth < maxDepth) {
      const computedStyle = window.getComputedStyle(scrollableContainer);
      if (['auto', 'scroll'].includes(computedStyle.overflow) || ['auto', 'scroll'].includes(computedStyle.overflowY)) break;
      scrollableContainer = scrollableContainer.parentElement;
      depth++;
    }
    return scrollableContainer;
  }

  /**
   * Attaches some events when the Popupmenu is opened.
   * Call this method from inside your extended component whenever "open" events should be applied.
   * @returns {void}
   */
  addOpenEvents() {
    requestAnimationTimeout(() => {
      const cardContent = getClosest((this as any)?.alignTarget, 'ids-card')?.shadowRoot?.querySelector('.ids-card-content');

      if (cardContent) {
        this.offEvent('scroll');
        this.onEvent('scroll', cardContent, (e: Event) => {
          this.onOutsideClick?.(e);
        });
      }

      // Attach a click handler to the window for detecting clicks outside the popup.
      // If these aren't captured by a popup, the menu will close.
      this.#popupContainer = getClosest(this, 'ids-scroll-container, .scrollable, ids-container, ids-modal, ids-data-grid, ids-action-panel, ids-splitter, ids-splitter-pane');
      const dropdown = getClosest(this, 'ids-dropdown');
      const nodeName: string = this.#popupContainer?.nodeName || '';
      switch (nodeName) {
        case 'IDS-CONTAINER':
          this.#popupContainer = (this.#popupContainer as any).container;
          break;
        case 'IDS-DATA-GRID':
          this.#popupContainer = this.#popupContainer!.shadowRoot?.querySelector('.ids-data-grid-wrapper .ids-data-grid') || null;
          break;
        case 'IDS-MODAL':
          this.#popupContainer = this.#popupContainer!.shadowRoot?.querySelector('ids-popup .ids-modal-container .has-scrollbar') || null;
          if (dropdown) {
            this.#popupContainer = this.#findScrollableContainer(dropdown, this.#popupContainer) || this.#popupContainer;
          }
          break;
        case 'IDS-ACTION-PANEL':
          this.#popupContainer = this.#popupContainer!.shadowRoot?.querySelector('ids-popup .ids-modal-container .ids-modal-content') || null;
          if (dropdown) {
            this.#popupContainer = this.#findScrollableContainer(dropdown, this.#popupContainer) || this.#popupContainer;
          }
          break;
        case 'IDS-SPLITTER-PANE':
          this.#popupContainer = this.#popupContainer!.shadowRoot?.querySelector('.ids-splitter-pane') || null;
          break;
        default:
          break;
      }

      if (this.#popupContainer) {
        this.offEvent('scroll.popup');
        this.onEvent('scroll.popup', this.#popupContainer, (e: Event) => {
          this.onOutsideClick?.(e);
        });
      }

      this.offEvent('scroll.document');
      this.onEvent('scroll.document', document, (e: Event) => {
        if (!(this as any).closeOnScroll) return;

        const dropdownElem = getClosest(this, 'ids-dropdown');
        const isMobile = isWidthBelow('md').matches;
        if (dropdownElem && isMobile) return;

        this.onOutsideClick?.(e);
      });

      this.offEvent('wheel.document');
      this.onEvent('wheel.document', document, (e: WheelEvent) => {
        if (!(this as any).closeOnScroll) return;
        if ((e?.target as any)?.nodeName?.substring(0, 8) === 'IDS-MENU') return;
        if ((e?.target as any)?.nodeName === 'IDS-THEME-SWITCHER') return;
        const isMenuItem = (e?.target as any)?.closest('ids-menu-item') || (e?.target as any)?.closest('ids-menu') || (e?.target as any)?.closest('ids-menu-button');
        if (isMenuItem) return;

        const dropdownElem = getClosest(this, 'ids-dropdown');
        const isMobile = isWidthBelow('md').matches;
        if (dropdownElem && isMobile) return;

        this.onOutsideClick?.(e);
      }, { passive: true });

      this.offEvent('touchmove.document');
      this.onEvent('touchmove.document', document, (e: Event) => {
        if (!(this as any).closeOnScroll) return;
        this.onOutsideClick?.(e);
      }, { passive: true });

      this.popupOpenEventsTarget ??= document.body;
      this.offEvent('click.toplevel', this.popupOpenEventsTarget);
      this.onEvent('click.toplevel', this.popupOpenEventsTarget, (e: Event) => {
        this.onOutsideClick?.(e);
      });

      this.#scrollContainer = getClosest(this, 'ids-scroll-container, .scrollable, ids-container, ids-modal, ids-data-grid, ids-action-panel, ids-splitter, ids-splitter-pane');
      if (this.#scrollContainer) {
        this.offEvent('scroll.toplevel', this.#scrollContainer);
        this.onEvent('scroll.toplevel', this.#scrollContainer, debounce((e: Event) => {
          this.onOutsideClick?.(e);
        }, 50));
      }
      this.hasOpenEvents = true;
      this.#currentPopupOpenEventsTarget = this.popupOpenEventsTarget;
    });
  }

  /**
   * Detaches some events when the Popupmenu is closed.
   * Call this method from inside your extended component whenever "open" events should be removed.
   * @param {boolean} forceClose - Whether to force close even if hasOpenEvents is false
   * @returns {void}
   */
  removeOpenEvents(forceClose = false) {
    if (!this.hasOpenEvents && !forceClose) {
      return;
    }

    if (!forceClose) {
      this.#popupContainer = getClosest(this, 'ids-scroll-container, .scrollable, ids-container, ids-modal, ids-data-grid, ids-action-panel, ids-splitter, ids-splitter-pane');
    } else {
      (this.onOutsideClick as any) = null;
      this.offEvent('scroll.popup', this.#popupContainer);
      this.offEvent('scroll.popup');
    }

    this.offEvent('scroll.document', document);
    this.offEvent('wheel.document', document);
    this.offEvent('touchmove.document', document);
    this.offEvent('click.toplevel', this.#currentPopupOpenEventsTarget);
    this.offEvent('scroll.toplevel', this.#scrollContainer);
    this.offEvent('wheel.toplevel', this.#scrollContainer);
    this.#scrollContainer = null;
    this.hasOpenEvents = false;
    this.popupOpenEventsTarget = null;
    this.#scrollContainer = null;
    this.#currentPopupOpenEventsTarget = null;
    this.#popupContainer = null;
  }
};

export default IdsPopupOpenEventsMixin;
